import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

import { Navigation, Pagination } from "swiper";

import "swiper/css";
import "swiper/css/pagination";
import SubText from "components/shared/SubText";
import { useDispatch, useSelector } from "react-redux";
import { removeLastWatch } from "redux/VideoPlayerRedux";
import VideoService from "services/video/VideoService";
import PickupSkeletons from "components/skeleton/PickupSkeletons";
import ErrorBoundary from "components/error-fallbacks/ErrorFallBack";

const PickupCard = lazy(() => import("pages/videoPage/home/shared/PickupCard"));

function PickupCarousel() {
  const dispatch = useDispatch<any>();
  const { lastWatched } = useSelector((state: any) => state?.video);
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const videosRef = useRef<any[]>([]);
  const [finalLastWatched, setFinalLastWatched] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<any>(false);

  useEffect(() => {
    setIsLoading(true);
    if (loggedInUser) {
      VideoService.getUserLastWatch().then((res) => {
        // Always show the server's saved watch history as-is - no merging
        // with the in-session redux state, so this row never shows stale
        // or locally-guessed values that drift from what's actually saved.
        let apiLastWatch: any[] = [
          ...res.watching_movies,
          ...res.watching_tv_shows,
          ...(res.watching_creator_videos ?? []),
        ];

        // The three lists above are grouped by content type, which hides
        // the real watch order across types. Sort by the saved
        // `updatedAt` timestamp so the most recently watched item - movie,
        // TV episode, or creator video - always shows up first.
        apiLastWatch = apiLastWatch
          .slice()
          .sort(
            (a, b) =>
              new Date(b?.updatedAt ?? 0).getTime() -
              new Date(a?.updatedAt ?? 0).getTime()
          );

        setFinalLastWatched(apiLastWatch);
        setIsLoading(false);
      });
    } else {
      setFinalLastWatched(videosRef.current);
      setIsLoading(false);
    }
  }, [videosRef.current]);

  useEffect(() => {
    setIsLoading(true);
    const data: any[] = lastWatched.filter((d: any) => {
      if (
        (d.currentTime / d.duration) * 100 < 95 &&
        (d.currentTime / d.duration) * 100 > 0
      ) {
        // console.log('save video');
        return d;
      } else {
        // console.log('delete video');
        dispatch(removeLastWatch(d.id));
      }
    });

    videosRef.current = data;
    setIsLoading(false);
  }, [lastWatched]);

  return (
    <>
      {!isLoading && (
        <>
          <ErrorBoundary>
            <Suspense fallback={<PickupSkeletons />}>
              {finalLastWatched.length > 0 && (
                <>
                  <div className="flex justify-between w-[95%] mt-5 max-sm:w-full">
                    <SubText
                      text="Pick up where you left off"
                      className="font-bold mt-2 max-sm:ml-4"
                    />
                  </div>

                  <Swiper
                    slidesPerView={7}
                    breakpoints={{
                      0: {
                        slidesPerView: 1,
                      },
                      380: {
                        slidesPerView: 1,
                      },
                      700: {
                        slidesPerView: 2,
                      },
                      1000: {
                        slidesPerView: 2,
                      },
                      1200: {
                        slidesPerView: 3,
                      },
                      1400: {
                        slidesPerView: 4,
                      },
                      1600: {
                        slidesPerView: 4,
                      },
                      1700: {
                        slidesPerView: 5,
                      },
                    }}
                    modules={[Pagination, Navigation]}
                    className="w-full mx-auto mt-5 mb-5 mySwiper"
                    navigation={true}
                  >
                    {finalLastWatched?.map((card: any, i: number) => (
                      <SwiperSlide key={i}>
                        <div className="mx-3 max-sm:mx-1">
                          <PickupCard {...card} />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </>
              )}
            </Suspense>
          </ErrorBoundary>
        </>
      )}
      {isLoading && <PickupSkeletons />}
    </>
  );
}

export default PickupCarousel;
